// src/pages/ValmikiProgram.jsx
// VALMIKI AI LAB — Program page (Page 3 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS live here (same design system as ValmikiHome.jsx).
//
// ANIMATION IDEA — every animation follows the program's own timeline:
//   • Hero tracker      : a gold line runs stage to stage (4 months → 2 months → 7 days → certification),
//                         each stage lights up when the line reaches it.
//   • Program journey   : bars fill left → right, in order, and their fill time is proportional to the
//                         stage length (4 months fills slowest, 7 days fills fastest).
//   • How you learn     : the 8-step method is "walked" — a gold line travels through each step in sequence.
//   • Internship        : deliverables tick off one by one, like a milestone review.
// Scroll-triggered sections play once when they enter the viewport. All motion is disabled for
// prefers-reduced-motion (the final state is shown instead).
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useEffect, useRef, useState } from "react";
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";
/* ───────────────────────── CONTENT ───────────────────────── */

const journey = [
  {
    size: "4 months",
    title: "Classroom learning",
    desc: "Classroom learning, labs, assignments, projects and portfolio.",
    w: "100%",
    tone: 1,
    d: 0.2,
    t: 1.8,
  },
  {
    size: "2 months",
    title: "Industry internship",
    desc: "Structured industry internship and practical deliverables.",
    w: "55%",
    tone: 2,
    d: 2.0,
    t: 1.0,
  },
  {
    size: "7 days",
    title: "Career bootcamp",
    desc: "AI Career & Placement Bootcamp.",
    w: "22%",
    tone: 3,
    d: 3.0,
    t: 0.6,
  },
  {
    size: "Final stage",
    title: "Assessment & support",
    desc: "Assessment, certification for eligible students and career/placement assistance.",
    w: "30%",
    tone: 4,
    d: 3.6,
    t: 0.5,
  },
];

// Compact version used by the hero tracker.
const heroStages = [
  { size: "4 months", label: "Classroom learning, labs and projects" },
  { size: "2 months", label: "Structured industry internship" },
  { size: "7 days", label: "AI Career & Placement Bootcamp" },
  { size: "Final stage", label: "Assessment, certification and career assistance" },
];

const learnModules = [
  "AI Foundations",
  "Generative AI",
  "Prompt Engineering",
  "Creative & Applied AI",
  "AI Automation",
  "Agentic AI",
  "Capstone Project",
];

const learningFlow = [
  "Theory",
  "Live demonstration",
  "Practical lab",
  "Business case",
  "Assignment",
  "Project",
  "Industry challenge",
  "Portfolio evidence",
];

const buildItems = [
  "AI-assisted workflows",
  "Automation projects",
  "Agentic AI workflows",
  "Business problem solutions",
  "Portfolio projects",
  "Capstone project",
];

const internshipRows = [
  { title: "Deliverables", state: "assigned" },
  { title: "Milestone reviews", state: "completed" },
  { title: "Implementation", state: "delivered" },
  { title: "Documentation", state: "submitted" },
  { title: "Final presentation", state: "presented" },
];

const bootcamp = [
  "Career positioning",
  "Professional resume",
  "LinkedIn profile",
  "Portfolio storytelling",
  "Interview preparation",
  "Mock interviews",
  "Corporate readiness",
];

const pathways = [
  "AI Associate",
  "Generative AI Associate",
  "AI Automation Associate",
  "AI Operations Associate",
  "AI Implementation Associate",
  "AI Content Specialist",
  "AI Marketing Associate",
  "Junior AI Consultant",
];

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}
.vl-white{background:var(--white);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-h3{font-family:var(--serif);font-weight:500;font-size:1.5rem;line-height:1.2;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}
.vl-muted{color:var(--slate);}
.vl-dark .vl-muted{color:var(--on-ink-mute);}
.vl-head{max-width:44rem;margin-bottom:56px;}
.vl-head .vl-h2{margin-bottom:16px;}

/* buttons */
@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}
.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
.vl-btn-line-light{--fill:transparent;--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);--hover-text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
.vl-final .vl-btn-ink{--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

/* ───────── hero ───────── */
.vl-hero{position:relative;overflow:hidden;background-color:var(--ink);color:var(--on-ink);padding:88px 0 104px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vl-hero-grid{position:relative;z-index:1;display:grid;grid-template-columns:1.1fr .9fr;gap:64px;align-items:center;}
.vl-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.4rem,5vw,4rem);line-height:1.05;
  letter-spacing:-.025em;margin-bottom:24px;}
.vl-hero .vl-lead{color:var(--on-ink-mute);margin-bottom:28px;}
.vl-format{display:inline-flex;align-items:center;gap:10px;margin-bottom:36px;
  padding:9px 14px;border:1px solid var(--line-ink);border-radius:2px;font:400 14px/1.4 var(--mono);color:var(--gold-hi);}
.vl-format i{width:6px;height:6px;border-radius:50%;background:var(--gold);display:block;}
.vl-hero-fx{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
.vl-hero-glow{position:absolute;right:-6%;top:6%;width:560px;height:560px;border-radius:50%;
  background:radial-gradient(circle,rgba(200,162,74,.18),transparent 65%);}

/* hero tracker: the gold line runs from stage to stage */
.vl-log{background:var(--ink-2);border:1px solid var(--line-ink);border-radius:10px;padding:22px 24px 22px;
  box-shadow:0 24px 60px rgba(0,0,0,.35);}
.vl-log-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  padding-bottom:14px;border-bottom:1px solid var(--line-ink);margin-bottom:22px;}
.vl-log-title{font:600 15px/1.3 var(--sans);}
.vl-log-tag{font:400 12px/1 var(--mono);color:var(--on-ink-mute);}
.vl-stages{position:relative;}
.vl-stage{position:relative;display:grid;grid-template-columns:24px 1fr;column-gap:18px;padding-bottom:30px;}
.vl-stage:last-child{padding-bottom:0;}
.vl-node{position:relative;z-index:1;width:22px;height:22px;border-radius:50%;
  border:1.5px solid rgba(243,245,248,.3);background:var(--ink-2);margin-top:2px;
  display:flex;align-items:center;justify-content:center;}
.vl-node svg{opacity:0;}
.vl-stage::after{content:"";position:absolute;left:10.5px;top:26px;bottom:2px;width:1.5px;
  background:var(--line-ink);}
.vl-stage:last-child::after{display:none;}
.vl-stage-line{position:absolute;left:10.5px;top:26px;bottom:2px;width:1.5px;background:var(--gold);
  transform-origin:top;z-index:1;}
.vl-stage:last-child .vl-stage-line{display:none;}
.vl-stage-size{font-family:var(--serif);font-size:1.5rem;line-height:1.15;letter-spacing:-.01em;}
.vl-stage-label{font-size:14px;color:var(--on-ink-mute);margin-top:2px;}
/* end state (also what reduced-motion users see) */
.vl-node{background:var(--gold);border-color:var(--gold);box-shadow:0 0 0 6px rgba(200,162,74,.16);}
.vl-node svg{opacity:1;}
@media (prefers-reduced-motion:no-preference){
  .vl-hero{animation:vl-grid 10s linear infinite;}
  .vl-hero-glow{animation:vl-glow 4s ease-in-out infinite alternate;}
  .vl-h1,.vl-hero .vl-lead,.vl-format,.vl-hero .vl-actions{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) both;}
  .vl-h1{animation-delay:.05s;}
  .vl-hero .vl-lead{animation-delay:.2s;}
  .vl-format{animation-delay:.32s;}
  .vl-hero .vl-actions{animation-delay:.44s;}
  .vl-log{animation:vl-panel-in .55s cubic-bezier(.2,.7,.2,1) .1s both;}
  .vl-hero .vl-btn::before{animation-duration:2s;}
  .vl-hero .vl-btn-gold{animation-duration:2.5s;}

  /* stage i lights up when the gold line reaches it */
  .vl-node{background:var(--ink-2);border-color:rgba(243,245,248,.3);box-shadow:none;
    animation:vl-node .45s ease forwards;animation-delay:calc(.7s + var(--i) * 1.2s);}
  .vl-node svg{animation:vl-show .01s linear forwards;animation-delay:calc(.9s + var(--i) * 1.2s);}
  .vl-stage-text{opacity:.4;animation:vl-lit .5s ease forwards;animation-delay:calc(.7s + var(--i) * 1.2s);}
  .vl-stage-line{transform:scaleY(0);animation:vl-run .9s cubic-bezier(.5,0,.3,1) forwards;
    animation-delay:calc(.95s + var(--i) * 1.2s);}
  /* after the run: the final node keeps a soft pulse */
  .vl-stage:last-child .vl-node{animation:vl-node .45s ease forwards,vl-pulse 2.6s ease-in-out 5.2s infinite;
    animation-delay:calc(.7s + var(--i) * 1.2s),5.2s;}
}
@keyframes vl-grid{to{background-position:56px 56px,56px 56px;}}
@keyframes vl-glow{to{transform:translate(-50px,40px) scale(1.12);}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-panel-in{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:none;}}
@keyframes vl-node{to{background:var(--gold);border-color:var(--gold);box-shadow:0 0 0 6px rgba(200,162,74,.16);}}
@keyframes vl-show{to{opacity:1;}}
@keyframes vl-lit{to{opacity:1;}}
@keyframes vl-run{to{transform:scaleY(1);}}
@keyframes vl-pulse{0%,100%{box-shadow:0 0 0 6px rgba(200,162,74,.16);}50%{box-shadow:0 0 0 12px rgba(200,162,74,0);}}

/* ───────── program journey (bars fill in order, time proportional to stage length) ───────── */
.vl-journey{display:grid;grid-template-columns:4fr 2fr 1.1fr 1.6fr;gap:6px;}
.vl-jbar-wrap{height:12px;background:rgba(11,22,38,.08);}
.vl-jbar{height:100%;background:var(--gold);transform-origin:left;}
.vl-jstage[data-tone="2"] .vl-jbar{background:rgba(200,162,74,.62);}
.vl-jstage[data-tone="3"] .vl-jbar{background:var(--ink);}
.vl-jstage[data-tone="4"] .vl-jbar-wrap{background:transparent;border:1.5px solid var(--gold);height:12px;}
.vl-jstage[data-tone="4"] .vl-jbar{background:repeating-linear-gradient(90deg,var(--gold) 0 6px,transparent 6px 10px);}
.vl-jsize{font-family:var(--serif);font-size:clamp(1.9rem,3.2vw,2.6rem);line-height:1;margin:22px 0 8px;letter-spacing:-.02em;}
.vl-jtitle{font:600 15px/1.4 var(--sans);margin-bottom:6px;}
.vl-jdesc{font-size:15px;color:var(--slate);max-width:26ch;}
@media (prefers-reduced-motion:no-preference){
  .vl-jbar{transition:transform calc(var(--t) * 1s) linear calc(var(--d) * 1s);}
  .vl-jtext{transition:opacity .5s ease calc(var(--d) * 1s),transform .5s ease calc(var(--d) * 1s);}
  .vl-journey:not(.in) .vl-jbar{transform:scaleX(0);}
  .vl-journey:not(.in) .vl-jtext{opacity:0;transform:translateY(10px);}
}

/* ───────── what you will learn ───────── */
.vl-modules{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--line);}
.vl-module{display:flex;align-items:baseline;gap:20px;padding:26px 0;border-bottom:1px solid var(--line);
  font-family:var(--serif);font-size:1.5rem;line-height:1.25;}
.vl-module:nth-child(even){padding-left:40px;border-left:1px solid var(--line);}
.vl-module:nth-child(odd){padding-right:40px;}
.vl-module-n{flex:none;font:400 12.5px/1 var(--mono);color:var(--slate);width:24px;}
.vl-module-cap{color:var(--gold-deep);font-weight:600;}
.vl-module-tag{margin-left:auto;font:400 12px/1 var(--mono);color:var(--gold-deep);
  border:1px solid var(--gold);padding:5px 8px;border-radius:2px;white-space:nowrap;}

/* ───────── how you learn (a gold line walks through the 8 steps) ───────── */
.vl-flow{display:grid;grid-template-columns:repeat(8,1fr);}
.vl-flow li{position:relative;padding:28px 14px 0 0;border-top:1.5px solid var(--line-ink);}
.vl-flow li::after{content:"";position:absolute;top:-1.5px;left:0;right:0;height:1.5px;background:var(--gold);transform-origin:left;}
.vl-flow li::before{content:"";position:absolute;top:-6px;left:0;width:10px;height:10px;border-radius:50%;background:var(--gold);z-index:1;}
.vl-flow li:last-child::after{right:auto;width:0;}
.vl-flow-n{display:block;font:400 12.5px/1 var(--mono);color:var(--on-ink-mute);margin-bottom:10px;}
.vl-flow-t{font-family:var(--serif);font-size:1.25rem;line-height:1.2;}
.vl-flow li:last-child .vl-flow-t{color:var(--gold-hi);}
@media (prefers-reduced-motion:no-preference){
  .vl-flow li::after{transition:transform .35s cubic-bezier(.5,0,.3,1) calc(var(--i) * .4s + .15s);}
  .vl-flow li::before{transition:transform .3s ease calc(var(--i) * .4s),background .3s ease calc(var(--i) * .4s);}
  .vl-flow-n,.vl-flow-t{transition:opacity .4s ease calc(var(--i) * .4s + .1s);}
  .vl-flow:not(.in) li::after{transform:scaleX(0);}
  .vl-flow:not(.in) li::before{transform:scale(0);}
  .vl-flow:not(.in) .vl-flow-n,.vl-flow:not(.in) .vl-flow-t{opacity:.25;}
}

/* ───────── what you build ───────── */
.vl-build{display:grid;grid-template-columns:repeat(3,1fr);gap:0 32px;}
.vl-build li{padding:28px 0 30px;border-top:2px solid var(--ink);font-family:var(--serif);font-size:1.5rem;line-height:1.25;}
.vl-build li:last-child{border-top-color:var(--gold);}
.vl-build-note{margin-top:36px;color:var(--slate);}

/* ───────── internship ───────── */
.vl-split{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center;}
.vl-split .vl-h2{margin-bottom:24px;}
.vl-log-light{background:var(--ink);color:var(--on-ink);}
.vl-log-list .vl-log-row{display:flex;align-items:center;gap:14px;padding:15px 0;border-bottom:1px solid var(--line-ink);}
.vl-log-list .vl-log-row:last-child{border-bottom:0;}
.vl-tick{flex:none;width:22px;height:22px;border-radius:50%;border:1.5px solid var(--gold);
  display:flex;align-items:center;justify-content:center;}
.vl-log-name{flex:1;font-size:15px;font-weight:500;}
.vl-log-state{font:400 12.5px/1 var(--mono);color:var(--gold-hi);text-align:right;}
.vl-log-list{margin-top:-8px;}
@media (prefers-reduced-motion:no-preference){
  .vl-log-row{transition:opacity .5s ease calc(var(--i) * .55s + .2s),transform .5s cubic-bezier(.2,.7,.2,1) calc(var(--i) * .55s + .2s);}
  .vl-log-row .vl-tick path{stroke-dasharray:20;transition:stroke-dashoffset .4s ease calc(var(--i) * .55s + .5s);}
  .vl-log-list:not(.in) .vl-log-row{opacity:0;transform:translateY(8px);}
  .vl-log-list:not(.in) .vl-tick path{stroke-dashoffset:20;}
}

/* ───────── bootcamp ───────── */
.vl-boot{display:grid;grid-template-columns:.8fr 1.2fr;gap:72px;align-items:start;}
.vl-boot-num{font-family:var(--serif);font-weight:500;font-size:clamp(4.5rem,11vw,8rem);line-height:.9;letter-spacing:-.04em;color:var(--gold-hi);}
.vl-boot-unit{font-family:var(--serif);font-size:1.75rem;margin:8px 0 20px;}
.vl-boot-list{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--line-ink);}
.vl-boot-list li{padding:20px 0;border-bottom:1px solid var(--line-ink);font-size:17px;padding-right:20px;}
.vl-boot-list li:nth-child(even){padding-left:24px;border-left:1px solid var(--line-ink);}

/* ───────── pathways ───────── */
.vl-paths{display:grid;grid-template-columns:repeat(4,1fr);gap:0 28px;}
.vl-paths li{padding:22px 0 26px;border-top:1.5px solid var(--ink);font-family:var(--serif);font-size:1.3rem;line-height:1.25;}
.vl-disclaimer{margin-top:40px;font-size:13.5px;color:var(--slate);max-width:70ch;}

/* ───────── final cta ───────── */
.vl-final{background:var(--gold);color:var(--ink);padding:96px 0;}
.vl-final .vl-h2{font-size:clamp(2.1rem,4.6vw,3.6rem);max-width:19em;margin-bottom:40px;}

/* footer */
.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

/* responsive */
@media (max-width:1000px){
  .vl-flow{grid-template-columns:repeat(4,1fr);row-gap:36px;}
  .vl-paths{grid-template-columns:repeat(2,1fr);}
  .vl-build{grid-template-columns:repeat(2,1fr);}
}
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-hero{padding:56px 0 72px;}
  .vl-hero-grid,.vl-split,.vl-boot{grid-template-columns:1fr;gap:48px;}
  .vl-journey{grid-template-columns:1fr;gap:36px;}
  .vl-jbar-wrap{width:var(--w);}
  .vl-jsize{margin-top:16px;}
  .vl-modules{grid-template-columns:1fr;}
  .vl-module,.vl-module:nth-child(even),.vl-module:nth-child(odd){padding:22px 0;border-left:0;}
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-actions .vl-btn{width:100%;}
  .vl-flow{grid-template-columns:repeat(2,1fr);}
  .vl-build,.vl-paths,.vl-boot-list{grid-template-columns:1fr;}
  .vl-boot-list li:nth-child(even){padding-left:0;border-left:0;}
  .vl-log-row{flex-wrap:wrap;row-gap:4px;}
  .vl-log-state{flex-basis:100%;text-align:left;padding-left:36px;}
}
`;

/* ───────────────────────── HELPERS ───────────────────────── */

// Adds `seen = true` the first time the element scrolls into view (plays scroll animations once).
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

const CheckPath = ({ stroke = "#0B1626" }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Tick = () => (
  <span className="vl-tick" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke="#E0BC62" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

/* ───────────────────── NAVBAR + FOOTER (inline) ───────────────────── */

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

// PROGRAM is the active page here.
const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program, current: true },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* ───────────────────────── PAGE ───────────────────────── */

export default function ValmikiProgram() {
  const [journeyRef, journeySeen] = useInView(0.35);
  const [flowRef, flowSeen] = useInView(0.35);
  const [internRef, internSeen] = useInView(0.35);

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* HERO */}
        <section className="vl-hero" aria-labelledby="vl-prog-title">
          <div className="vl-hero-fx" aria-hidden="true"><span className="vl-hero-glow" /></div>
          <div className="vl-wrap vl-hero-grid">
            <div>
              <h1 className="vl-h1" id="vl-prog-title">AI Career Development Program</h1>
              <p className="vl-lead">
                A structured journey from AI learning to practical projects, internship and career readiness.
              </p>
              <p className="vl-format"><i aria-hidden="true" />2 Days per Week • 3 Hours per Session</p>
              <div className="vl-actions">
                <a className="vl-btn vl-btn-gold" href="#vl-journey">See the Journey</a>
                <a className="vl-btn vl-btn-line-light" href={VALMIKI_ROUTES.contact}>Contact Us</a>
              </div>
            </div>

            <aside className="vl-log" aria-label="The program stages, in order">
              <div className="vl-log-head">
                <span className="vl-log-title">Program journey</span>
                <span className="vl-log-tag">4 stages</span>
              </div>
              <ol className="vl-stages">
                {heroStages.map((s, i) => (
                  <li key={s.size} className="vl-stage" style={{ "--i": i }}>
                    <span className="vl-node"><CheckPath /></span>
                    <span className="vl-stage-line" />
                    <div className="vl-stage-text">
                      <p className="vl-stage-size">{s.size}</p>
                      <p className="vl-stage-label">{s.label}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        </section>

        {/* PROGRAM JOURNEY */}
        <section className="vl-section" id="vl-journey" aria-labelledby="vl-journey-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-journey-title">Program journey</h2>
              <p className="vl-lead vl-muted">
                Each stage builds on the one before it. The bars below show how long each one takes.
              </p>
            </div>
            <ol ref={journeyRef} className={`vl-journey${journeySeen ? " in" : ""}`} aria-label="Program stages, in order">
              {journey.map((s) => (
                <li
                  key={s.size}
                  className="vl-jstage"
                  data-tone={s.tone}
                  style={{ "--w": s.w, "--d": s.d, "--t": s.t }}
                >
                  <div className="vl-jbar-wrap"><div className="vl-jbar" /></div>
                  <div className="vl-jtext">
                    <p className="vl-jsize">{s.size}</p>
                    <p className="vl-jtitle">{s.title}</p>
                    <p className="vl-jdesc">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* WHAT YOU WILL LEARN */}
        <section className="vl-section vl-white" aria-labelledby="vl-learn-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-learn-title">What you will learn</h2>
            </div>
            <ol className="vl-modules">
              {learnModules.map((m, i) => {
                const last = i === learnModules.length - 1;
                return (
                  <li key={m} className="vl-module">
                    <span className="vl-module-n">{String(i + 1).padStart(2, "0")}</span>
                    <span className={last ? "vl-module-cap" : undefined}>{m}</span>
                    {last && <span className="vl-module-tag">final project</span>}
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        {/* HOW YOU LEARN */}
        <section className="vl-section vl-dark" aria-labelledby="vl-how-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-how-title">How you learn</h2>
              <p className="vl-lead vl-muted">
                Every topic follows the same path, from an idea to evidence you can show.
              </p>
            </div>
            <ol ref={flowRef} className={`vl-flow${flowSeen ? " in" : ""}`} aria-label="The learning method, in order">
              {learningFlow.map((step, i) => (
                <li key={step} style={{ "--i": i }}>
                  <span className="vl-flow-n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="vl-flow-t">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* WHAT YOU BUILD */}
        <section className="vl-section" aria-labelledby="vl-build-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-build-title">What you build</h2>
            </div>
            <ul className="vl-build">
              {buildItems.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <p className="vl-build-note">Every project is documented as portfolio evidence of real work.</p>
          </div>
        </section>

        {/* INTERNSHIP */}
        <section className="vl-section vl-white" aria-labelledby="vl-intern-title">
          <div className="vl-wrap vl-split">
            <div>
              <h2 className="vl-h2" id="vl-intern-title">Internship</h2>
              <p className="vl-lead">
                A two-month structured internship focused on deliverables, milestone reviews,
                implementation, documentation and final presentation.
              </p>
            </div>
            <aside className="vl-log vl-log-light" aria-label="What the internship covers">
              <div className="vl-log-head">
                <span className="vl-log-title">Internship checkpoints</span>
                <span className="vl-log-tag">2 months</span>
              </div>
              <ul ref={internRef} className={`vl-log-list${internSeen ? " in" : ""}`}>
                {internshipRows.map((r, i) => (
                  <li key={r.title} className="vl-log-row" style={{ "--i": i }}>
                    <Tick />
                    <span className="vl-log-name">{r.title}</span>
                    <span className="vl-log-state">{r.state}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        {/* CAREER BOOTCAMP */}
        <section className="vl-section vl-dark" aria-labelledby="vl-boot-title">
          <div className="vl-wrap vl-boot">
            <div>
              <p className="vl-boot-num" aria-hidden="true">7</p>
              <p className="vl-boot-unit">days</p>
              <h2 className="vl-h2" id="vl-boot-title">Career Bootcamp</h2>
            </div>
            <ul className="vl-boot-list">
              {bootcamp.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </div>
        </section>

        {/* CAREER PATHWAYS */}
        <section className="vl-section" aria-labelledby="vl-paths-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-paths-title">Career pathways</h2>
              <p className="vl-lead vl-muted">Potential pathways may include:</p>
            </div>
            <ul className="vl-paths">
              {pathways.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <p className="vl-disclaimer">
              Career outcomes and opportunities vary by employer, market demand, student performance,
              skills, portfolio and location.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="vl-final" aria-labelledby="vl-final-title">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-final-title">
              Start the journey from AI learning to a career.
            </h2>
            <div className="vl-actions">
              <a className="vl-btn vl-btn-ink" href={VALMIKI_ROUTES.contact}>Contact Us</a>
              <a className="vl-btn vl-btn-line-ink" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}